import React, { useMemo } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { buildPalette } from "./palette";
import { buildTypography } from "./typography";
import { useConfig } from "./ConfigContext";

/**
 * The Mantis Free "theme constant" — `config.js`'s `fontFamily`, ported verbatim (docs/337). We
 * don't add the `@fontsource/public-sans` dependency Mantis itself ships (an extra dep just for a
 * self-hosted font file, out of scope for a look/feel evaluation) — the font-stack fallback to
 * the OS system sans-serif is visually close enough and needs zero extra bytes.
 */
const FONT_FAMILY = `'Public Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`;

/**
 * Builds the ONE MUI theme this app mounts — see the folder README for what was vendored
 * (typography, near-verbatim) vs. reconstructed (palette — the real Mantis palette pipeline
 * needs `@ant-design/colors` + a bespoke tonal-palette mapper this evaluation build skipped).
 */
export function buildMantisTheme(mode: "light" | "dark") {
  return createTheme({
    palette: buildPalette(mode),
    typography: buildTypography(FONT_FAMILY),
    shape: { borderRadius: 8 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6 },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: "inherit" },
      },
    },
  });
}

/**
 * Mounts exactly ONE `<ThemeProvider>` + ONE `<CssBaseline/>` (docs/337 gotcha: two of either
 * fights itself) — the single place `main.tsx` wraps `<App/>` in the Mantis look.
 */
export function MantisThemeProvider({ children }: { children: React.ReactNode }) {
  const { mode } = useConfig();
  const theme = useMemo(() => buildMantisTheme(mode), [mode]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
